// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Post-login onboarding  (/welcome/terms — Legal & Freshness Charter)
// The plain-language summary shown on the charter screen. EVERY point is taken from the published
// documents (pages/TermsAndConditions.tsx, pages/PrivacyPolicy.tsx, the site's disclaimer strip);
// the clause numbers say where. Do not add a commitment here that the full documents don't make —
// in particular the FSSAI licence is "in progress", not issued (Terms section 6.2).
// When the documents change, update this file AND bump POLICY_VERSION (utils/policy.ts and
// backend/app/domain/consent.py) so customers are asked to accept again.
// ─────────────────────────────────────────────────────────────────────────────
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY, COMPANY_NAME } from "./legalContact";

export interface CharterSection {
  id: "terms" | "privacy" | "disclaimer";
  title: string;
  intro: string;
  points: { heading: string; body: string; clause: string }[];
  fullDocument: { label: string; to: string };
}

export const CHARTER_INTRO =
  "A plain-language summary of what you agree to. The full Terms and Privacy Policy are the binding documents; each section links to them.";

export const CHARTER_SECTIONS: CharterSection[] = [
  {
    id: "terms",
    title: "Terms of Service & fulfilment",
    intro: `These terms are between you and ${COMPANY_NAME}, which operates ZenFresh.`,
    fullDocument: { label: "Read the full Terms of Service", to: "/terms" },
    points: [
      {
        heading: "Your order is an offer",
        body: "Orders are subject to acceptance. ZenFresh may decline one, for example if your address is outside the serviceable area, payment can't be verified, or fraud is suspected, and will refund anything already collected.",
        clause: "Terms, section 4.1",
      },
      {
        heading: "If an item isn't available",
        body: "You'll be told before confirmation, or given the choice to substitute, wait for the next delivery date, or cancel that part of the order for a full refund credited within 4 business days to the original payment method (or as RBI guidelines prescribe, whichever is sooner).",
        clause: "Terms, section 4.2",
      },
      {
        heading: "Promotional codes",
        body: "A code can be used once per buyer, can't be combined with other offers unless stated, and may be withdrawn if it is abused.",
        clause: "Terms, section 4.3",
      },
      {
        heading: "Price and payment",
        body: "Prices are as shown when you order, including applicable taxes unless stated. The total, including any delivery charge, is shown in your cart before you confirm. You pay at ordering through RBI-authorised payment providers.",
        clause: "Terms, section 5.1, 5.2, 5.4",
      },
      {
        heading: "Where the seafood comes from",
        body: "Fresh catch sourced daily from local fishing harbours in India, unless a product listing says otherwise. Farmed or imported items are labelled with their origin.",
        clause: "Terms, section 6.1",
      },
    ],
  },
  {
    id: "privacy",
    title: "Privacy & data processing",
    intro: "How we handle the details you give us. Optional messages and analytics stay off unless you choose them.",
    fullDocument: { label: "Read the full Privacy Policy", to: "/privacy" },
    points: [
      {
        heading: "What we collect and why",
        body: "The details you give us, such as your mobile number, name, photo, delivery addresses and order details, to sign you in, deliver your orders and give you support.",
        clause: "Privacy, section 3, 4",
      },
      {
        heading: "How long we keep it",
        body: "Only as long as needed to provide the service and meet legal, accounting and tax duties. If you delete your account or withdraw consent, we delete or anonymise your data unless the law requires us to keep it.",
        clause: "Privacy, section 8",
      },
      {
        heading: "Your rights",
        body: "You can ask to see a summary of your data, correct it, have it erased (subject to legal retention), withdraw consent at any time, raise a grievance, and nominate someone to act for you.",
        clause: "Privacy, section 14",
      },
      {
        heading: "Reaching us",
        body: `Write to ${CONTACT_EMAIL} or call ${CONTACT_PHONE_DISPLAY}. The Grievance Officer's details are in the Privacy Policy.`,
        clause: "Privacy, section 15",
      },
    ],
  },
  {
    id: "disclaimer",
    title: "Food safety & availability disclaimer",
    intro: "ZenFresh is an advance-booking platform: seafood availability is subject to daily market conditions.",
    fullDocument: { label: "Read Terms section 6 on sourcing and food safety", to: "/terms" },
    points: [
      {
        heading: "Seasonal availability",
        body: "Availability can be affected by the annual government-mandated fishing ban along the Indian coast (typically in the monsoon), weather and catch volumes. Farmed or alternative species may be offered, clearly marked, or an item may be unavailable.",
        clause: "Terms, section 6.6",
      },
      {
        heading: "Allergens",
        body: "Seafood and shellfish are recognised allergens. If you have a food allergy or sensitivity, read the product listing and Terms section 6.5 before ordering.",
        clause: "Terms, section 6.5",
      },
      {
        heading: "Food-safety licence",
        body: "ZenFresh's FSSAI licence/registration is in progress. Once issued, the number will be shown on the platform and on delivery packaging.",
        clause: "Terms, section 6.2",
      },
    ],
  },
];
